// Story 5.1 — load theme token CSS into the document. The import is
// side-effect only; tokens are emitted as CSS custom properties on
// :root[data-theme="dark"] and :root[data-theme="light"]. The literal
// data-theme attribute on <html> in index.html selects the dark scope
// for now; Story 5.5 (CloudFront viewer-response Function) replaces
// the literal with a `__PVT_THEME__` marker that the function
// resolves per-request based on the `pvt_theme` cookie.
import './theme/tokens.css';

import { StrictMode, lazy, Suspense, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from 'react-router-dom';

// Story 5.2 — runtime theme glue. ThemeProvider is the single writer of
// <html data-theme>, localStorage["pvt:theme"], and the pvt_theme cookie.
// Wraps OUTSIDE BrowserRouter (between StrictMode and BrowserRouter) so the
// router never re-mounts the provider; theme is route-independent. Per-
// surface theme controls (admin top bar, voter / submitter settings sheets)
// land in later stories per Critical Scope Decision #10. The marketing
// landing at `/` does NOT render a theme toggle (Story 17.3 CSD #1 -
// dark-by-default, no toggle on the public surface).
import { ThemeProvider } from './theme/ThemeProvider';

// Story 5.4 - primary CTA primitive. Used by `<MarketingLanding>` for the
// "Create event" button.
import { Button } from './components/Button';

// Story 5.6 — BrandingProvider primitive lands. This import is unused at
// main.tsx (no JSX wrap; AC #6 + Critical Scope Decision #6 lock — Story
// 5.6 ships the primitive only). First per-route composers land in
// Stories 6.1 / 6.5 / 8.1 / 8.4 / 9.1 / 9.7 / 12.x; production builds
// will tree-shake the module until those stories add concrete consumers.
// The import is retained per AC #6 as a discoverable hand-off marker.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { BrandingProvider } from './branding/BrandingProvider';

// Story 1.9 — Code-split the admin tree into its own chunk (architecture §2.6).
// Voter / submitter / broadcast trees land in later epics and follow the same
// dynamic-import pattern. The path is `./admin/AdminApp` (no extension) — Vite
// resolves it to web/src/admin/AdminApp.tsx; appending `.tsx` would trip
// tsc strict mode under bundler resolution.
const AdminApp = lazy(() => import('./admin/AdminApp'));

// Story 5.8 - sibling lazy imports for the non-admin route groups
// (architecture §2.6 lines 258-271 chunk table). Real shells now live
// behind each lazy import (Stories 8.x, 9.x, 10.x, 12.x). The chunker
// rule in `web/vite.config.ts` routes each `web/src/<route-group>/*`
// module to its own named chunk.
// Story 18.10 — `VoterApp` + `SubmitterApp` lazy imports retired; the
// `/e/:eventId/vote/*` and `/e/:eventId/submit/*` routes now SPA-redirect
// to `/e/:eventId/join` via `<LegacyRedirect>`. Source files retained per
// CSD #6 (no file deletes; tree-shaking removes them from the bundle).
const BroadcastApp = lazy(() => import('./broadcast/BroadcastApp'));
const RevealControlApp = lazy(() => import('./revealControl/RevealControlApp'));
// Story 18.2 — `<JoinApp>` lazy chunk for `/e/:eventId/join/*`. Story
// 18.10 — the unified canonical surface; legacy `/vote/*` and `/submit/*`
// redirect here.
const JoinApp = lazy(() => import('./join/JoinApp'));

/**
 * Story 18.10 — `<LegacyRedirect>` SPA-level redirect wrapper for
 * `/e/:eventId/vote/*` and `/e/:eventId/submit/*`. Reads the live
 * `:eventId` path segment via `useParams`, preserves the query-string
 * via `useSearchParams`, but DISCARDS the trailing path segment per
 * CSD #5 (the `/join` SPA has no sub-routes). Uses `<Navigate replace>`
 * so the browser back button does not return to the legacy URL.
 *
 * AC #1 + AC #2.
 */
export function LegacyRedirect(): JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();
  const [search] = useSearchParams();
  const queryString = search.toString();
  const target =
    queryString.length > 0
      ? `/e/${eventId}/join?${queryString}`
      : `/e/${eventId}/join`;
  return <Navigate to={target} replace />;
}

// Story 5.1 — dev-only swatch page for visual review of theme tokens.
// Reachable at /theme-swatches?showSwatches=1 in `npm run dev`. The
// `import.meta.env.DEV` guard around `lazy(() => import(...))` is
// load-bearing: production builds inline `false` and Vite/Rollup
// tree-shakes the dynamic import (and the entire SwatchesPage chunk)
// out of the bundle. Without the guard, the lazy() call at module top
// level would always be reachable and Vite would always emit the
// chunk. Verified empirically (npm run build) — SwatchesPage chunk is
// absent from web/dist/ when DEV=false.
const SwatchesPage = import.meta.env.DEV
  ? lazy(() => import('./theme/swatches/SwatchesPage'))
  : null;

// Story 17.3 - marketing landing styles. Skip-to-main pattern matches
// Story 5.1 a11y baseline (visually hidden until focused). H1 size is
// driven by a CSS media query against `--text-display-md-size` (<768px)
// and `--text-display-lg-size` (>=768px) - no JS resize listener.
const MARKETING_LANDING_STYLE = `
.skip-to-main {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
.skip-to-main:focus {
  position: fixed;
  top: var(--space-2);
  left: var(--space-2);
  width: auto;
  height: auto;
  padding: var(--space-2) var(--space-3);
  margin: 0;
  overflow: visible;
  clip: auto;
  background: var(--bg-surface-raised);
  color: var(--text-primary);
  border: 2px solid var(--brand-primary);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-weight: 600;
  z-index: 1000;
  outline: none;
  text-decoration: none;
}
.pvt-marketing-h1 {
  font-family: var(--font-display);
  font-weight: var(--text-display-md-weight);
  font-size: var(--text-display-md-size);
  line-height: var(--text-display-md-line);
  color: var(--text-primary);
  margin: 0;
}
@media (min-width: 768px) {
  .pvt-marketing-h1 {
    font-size: var(--text-display-lg-size);
    line-height: var(--text-display-lg-line);
    font-weight: var(--text-display-lg-weight);
  }
}
`;

/**
 * Cheap, synchronous detection of an active admin session.
 *
 * `<AuthProvider>` (web/src/admin/AuthProvider.tsx) builds a `UserManager`
 * from `oidc-client-ts` with all defaults — i.e. the user blob lands in
 * `sessionStorage` under the key `oidc.user:<authority>:<clientId>` (the
 * library's `WebStorageStateStore` prefixes `oidc.` to its `user:...` key).
 * The same shape is documented + asserted in `web/tests/e2e/auth.ts`. We
 * re-derive the key here so the public marketing landing can answer
 * "is the visitor an authenticated admin?" without importing the admin
 * chunk (oidc-client-ts must stay isolated to the admin chunk per
 * `web/vite.config.ts` + `scripts/__tests__/assert-bundle-shape.test.ts`).
 *
 * Returns `false` for any error path: missing config, unparseable JSON,
 * missing `expires_at`, or `expires_at` already past. Never throws — a
 * thrown read here would break the public landing for unauth visitors.
 */
function hasActiveAdminSession(): boolean {
  try {
    const cfg = window.__PVT_CONFIG__;
    if (!cfg || !cfg.region || !cfg.userPoolId || !cfg.userPoolClientId) {
      return false;
    }
    const authority = `https://cognito-idp.${cfg.region}.amazonaws.com/${cfg.userPoolId}`;
    const key = `oidc.user:${authority}:${cfg.userPoolClientId}`;
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { expires_at?: number } | null;
    if (!parsed || typeof parsed.expires_at !== 'number') return false;
    // `expires_at` is Unix-seconds (oidc-client-ts convention). Compare
    // against `Date.now() / 1000` with a small skew tolerance of 0 — we
    // intentionally treat a freshly-expired token as "not signed in"
    // rather than redirecting into a stale admin session.
    return parsed.expires_at > Date.now() / 1000;
  } catch {
    // sessionStorage may throw in sandboxed iframes; JSON.parse may
    // throw on corrupt entries. Treat every error as "not signed in".
    return false;
  }
}

const ADMIN_LANDING_PATH = '/admin/events';
const ADMIN_LOGIN_ENTRY_PATH = '/admin';

export function MarketingLanding() {
  // Synchronous initial detection so logged-in admins never see the
  // public landing flash before the redirect fires. `useState`
  // initializer runs once on mount; the effect below performs the
  // actual navigation. SSR is not in play (Vite SPA) so `window` /
  // `sessionStorage` access at init time is safe.
  const [isAuthedAdmin] = useState<boolean>(() => hasActiveAdminSession());

  useEffect(() => {
    if (isAuthedAdmin) {
      window.location.assign(ADMIN_LANDING_PATH);
    }
  }, [isAuthedAdmin]);

  // Render-suppression while the redirect is in flight. We deliberately
  // render NOTHING (not even a spinner) for authed admins — the redirect
  // is synchronous from React's perspective (effect fires the same tick
  // as the first commit) and any visible flash here would be the very
  // CTA flicker we are trying to avoid.
  if (isAuthedAdmin) {
    return null;
  }

  const ctaLabel = 'Login';
  const ctaHref = ADMIN_LOGIN_ENTRY_PATH;

  return (
    <>
      <style data-testid="marketing-landing-styles">{MARKETING_LANDING_STYLE}</style>
      <a href="#main" className="skip-to-main">
        Skip to main content
      </a>
      <main
        id="main"
        style={{
          display: 'grid',
          placeItems: 'center',
          minHeight: '100dvh',
          // Story 17.3 AC #4 - dark canvas literal so the marketing
          // surface ignores OS prefers-color-scheme. Matches the dark-
          // theme `--bg-canvas` token (#0B0E13) for visual consistency.
          background: '#0B0E13',
          padding: 'var(--space-5)',
          fontFamily: 'var(--font-sans)',
          color: 'var(--text-primary)',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-5)',
            maxWidth: '880px',
            textAlign: 'center',
          }}
        >
          <h1 className="pvt-marketing-h1">Photo Contest</h1>
          <p
            style={{
              fontSize: 'var(--text-lg-size)',
              lineHeight: 'var(--text-lg-line)',
              color: 'var(--text-primary)',
              margin: 0,
            }}
          >
            Live photo contests for your event, scored in real time.
          </p>
          <Button
            variant="primary"
            onClick={() => {
              window.location.assign(ctaHref);
            }}
            data-href={ctaHref}
          >
            {ctaLabel}
          </Button>
          <p
            style={{
              fontSize: 'var(--text-sm-size)',
              lineHeight: 'var(--text-sm-line)',
              color: 'var(--text-secondary)',
              margin: 0,
            }}
          >
            Have a join link? Follow the link your event organizer sent you.
          </p>
        </div>
      </main>
      {import.meta.env.DEV && (
        <footer
          style={{
            position: 'fixed',
            bottom: 'var(--space-3)',
            right: 'var(--space-3)',
            fontSize: 'var(--text-xs-size)',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-sans)',
          }}
        >
          <a
            href="/theme-swatches?showSwatches=1"
            style={{ color: 'var(--text-muted)' }}
          >
            View component gallery (DEV)
          </a>
        </footer>
      )}
    </>
  );
}

function NotFound() {
  return (
    <main>
      <h1>Not found</h1>
      <p>
        <a href="/">Home</a>
      </p>
    </main>
  );
}

// Async bootstrap — fetch /config.json BEFORE rendering React. The SPA
// cannot do anything useful without knowing where to authenticate, so we
// block the first render on the config fetch. /config.json is written by
// CDK's BucketDeployment (Source.jsonData) at deploy time.
async function bootstrap() {
  const res = await fetch('/config.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to load /config.json: ${res.status}`);
  }
  window.__PVT_CONFIG__ = await res.json();

  const root = document.getElementById('root');
  if (!root) {
    throw new Error('Root element #root missing from index.html');
  }

  createRoot(root).render(
    <StrictMode>
      <ThemeProvider>
        <BrowserRouter>
          <Suspense fallback={<p>Loading…</p>}>
            <Routes>
              <Route path="/" element={<MarketingLanding />} />
              {/* Story 5.8 — public route-group registry. Order
                  matters: revealControl is registered BEFORE
                  /admin/* so React Router's specificity-first
                  matching picks revealControl for the nested
                  `/admin/events/:eventId/reveal/*` path. CSD #2 +
                  CSD #4 lock this order. */}
              {/* Story 18.10 — legacy `/vote/*` and `/submit/*`
                  surfaces SPA-redirect to `/join` via
                  `<LegacyRedirect>`. Browser back button does NOT
                  return to the legacy URL (replace: true). The
                  trailing path segment is discarded per CSD #5; the
                  query-string is preserved for `?email=`,
                  `?expired=1`, etc. */}
              <Route
                path="/e/:eventId/vote/*"
                element={<LegacyRedirect />}
              />
              <Route
                path="/e/:eventId/submit/*"
                element={<LegacyRedirect />}
              />
              {/* Story 18.2 — unified canonical join surface (Story
                  18.10 makes it the only Voter/Submitter route). */}
              <Route path="/e/:eventId/join/*" element={<JoinApp />} />
              <Route path="/e/:eventId/broadcast/*" element={<BroadcastApp />} />
              <Route
                path="/admin/events/:eventId/reveal/*"
                element={<RevealControlApp />}
              />
              <Route path="/admin/*" element={<AdminApp />} />
              {import.meta.env.DEV && SwatchesPage && (
                <Route
                  path="/theme-swatches"
                  element={
                    <Suspense fallback={<p>Loading swatches…</p>}>
                      <SwatchesPage />
                    </Suspense>
                  }
                />
              )}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ThemeProvider>
    </StrictMode>,
  );
}

void bootstrap();
